/**
 * useSchoolAssessmentAnalyzeAdapter — vapor upload + `processSchoolAssessment` for AI prefill.
 *
 * Same batch pipeline as legacy `SchoolAssessmentTopicForm` / `useSendTutorMessageAdapter`.
 * Surfaces errors via `onError` (no legacy DialogM).
 */
import { useCallback, useMemo, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import moment from "moment"

import { useRequestsBatch } from "../../../../common/hooks/useRequestsBatch"
import { formIds } from "../../../../data/constants/formConstants"
import { apiIdle } from "../../../../data/redux/actions/api"
import { schoolAssessmentsService } from "../../../../data/services/schoolAssessmentsService"
import {
    toUISchoolAssessmentPreview,
    type ApiSchoolAssessmentProcessResult,
} from "../../adapters/parent/SchoolAssessments/schoolAssessmentProcessAdapter"
import { type UISchoolAssessmentPreview } from "../types"

type MediaImage = { uri: string; width?: number; height?: number; name?: string; type?: string }

export type UseSchoolAssessmentAnalyzeAdapterArgs = {
    studentId: string | number | null | undefined
    images: MediaImage[]
    pdf: string | null
    onSuccess: (preview: UISchoolAssessmentPreview) => void
    onError: (message: string) => void
}

export function useSchoolAssessmentAnalyzeAdapter(args: UseSchoolAssessmentAnalyzeAdapterArgs) {
    const { studentId, images, pdf, onSuccess, onError } = args
    const dispatch = useDispatch()
    const [analyzing, setAnalyzing] = useState(false)
    const startedRef = useRef(false)

    const filesToUpload = useMemo(() => {
        const date = moment().format("YYYY-MM-DD")
        const files: { uri: string; name: string; type: string }[] = []
        images.forEach((img, index) => {
            if (!img?.uri) return
            files.push({
                uri: img.uri,
                name: img.name || `image_${date}_${index}.jpg`,
                type: img.type || "image/jpeg",
            })
        })
        if (pdf) {
            files.push({ uri: pdf, name: `document_${date}.pdf`, type: "application/pdf" })
        }
        return files
    }, [images, pdf])

    const [, submitted, startRequests] = useRequestsBatch(
        [],
        filesToUpload,
        formIds.NEW_SCHOOL_ASSESSMENT,
        {
            service: schoolAssessmentsService.processSchoolAssessment,
            params: {},
            data: { student_id: studentId },
            tmpFilesKey: "attachment_files",
            tmpFilesFormatter: (tmpKey: string) => tmpKey,
            additionalData: [],
            onSuccessBeforeNext: (responseData: unknown) => {
                startedRef.current = false
                setAnalyzing(false)
                const payload = (responseData as { data?: ApiSchoolAssessmentProcessResult })?.data ?? responseData
                const preview = toUISchoolAssessmentPreview(payload as ApiSchoolAssessmentProcessResult)
                if (preview == null) {
                    onError("Analyse impossible. Réessaie avec une autre photo.")
                    return false
                }
                onSuccess(preview)
                return false
            },
            onError: (err: { message?: string } | undefined) => {
                startedRef.current = false
                setAnalyzing(false)
                onError(err?.message || "Une erreur est survenue pendant l'analyse.")
            },
        },
        [],
        true
    ) as [number, boolean, () => void]

    const canAnalyze = filesToUpload.length > 0 && studentId != null

    const onAnalyze = useCallback(() => {
        if (!canAnalyze || submitted || startedRef.current) return
        dispatch(apiIdle(schoolAssessmentsService.processSchoolAssessment) as never)
        startedRef.current = true
        setAnalyzing(true)
        startRequests()
    }, [canAnalyze, submitted, dispatch, startRequests])

    return {
        onAnalyze,
        analyzeLoading: analyzing || !!submitted,
        canAnalyze,
    }
}
